import { useCallback, useEffect, useState } from "react";
import { site } from "../../config/site";
import { api, type ItineraryEvent, type Me } from "../lib/api";
import { signOut, useSession } from "../lib/auth-client";
import { linkProps, navigate } from "../lib/router";
import { Footer, Header, Notice, Spinner } from "../components/Layout";
import { EventCard } from "../components/EventCard";
import { Onboarding } from "./Onboarding";

interface ItineraryData {
  guest: { id: string; fullName: string; status: string; partyMax: number };
  events: ItineraryEvent[];
}

/** Signed in, but nobody on the guest list matched their email or phone. */
function PendingReview({ name }: { name: string }) {
  return (
    <div className="animate-rise py-10 text-center">
      <p className="eyebrow">Hold tight</p>
      <h1 className="mt-4 font-display text-4xl leading-tight font-light sm:text-5xl">
        We're checking our list
      </h1>
      <p className="mx-auto mt-5 max-w-md leading-relaxed text-muted">
        Thanks, {name.split(" ")[0]} — we couldn't automatically match you to an invitation, so
        we've flagged it for {site.partnerOne} and {site.partnerTwo} to look at. You'll get an email
        as soon as your events are ready.
      </p>
      <p className="mx-auto mt-8 max-w-md text-sm text-muted">
        If you think this is a mistake — maybe you signed up with a different email than the one we
        wrote to — send us a note at{" "}
        <a
          href={`mailto:${site.contactEmail}`}
          className="text-ink underline decoration-line underline-offset-4"
        >
          {site.contactEmail}
        </a>
        .
      </p>
    </div>
  );
}

/** Matched to the list, but not invited to any active event yet. */
function NoEvents() {
  return (
    <div className="animate-rise py-10 text-center">
      <h1 className="font-display text-4xl font-light">Your events are coming</h1>
      <p className="mx-auto mt-5 max-w-md text-muted">
        We haven't finalised your part of the weekend yet. Check back soon — we'll email you when
        there's something to see.
      </p>
    </div>
  );
}

export function Itinerary() {
  const { data: session, isPending: sessionLoading } = useSession();
  const [me, setMe] = useState<Me | null>(null);
  const [data, setData] = useState<ItineraryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const meRes = await api.get<Me>("/api/me");
      setMe(meRes);
      if (!meRes.needsOnboarding) {
        setData(await api.get<ItineraryData>("/api/itinerary"));
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load your invitation.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (sessionLoading) return;
    if (!session) {
      navigate("/login", { replace: true });
      return;
    }
    void load();
  }, [session, sessionLoading, load]);

  if (sessionLoading || (loading && session)) {
    return (
      <div className="mx-auto max-w-3xl px-6">
        <Header />
        <Spinner label="Finding your invitation" />
      </div>
    );
  }

  if (!session) return null;

  const firstName = (data?.guest.fullName ?? me?.user.name ?? "").split(" ")[0];
  const answered = data?.events.filter((e) => e.rsvp !== null).length ?? 0;
  const total = data?.events.length ?? 0;

  return (
    <div className="mx-auto max-w-3xl px-6">
      <Header
        right={
          <>
            {me?.isAdmin && (
              <a {...linkProps("/admin")} className="btn btn-ghost text-sm">
                Admin
              </a>
            )}
            <button
              className="text-sm text-muted underline decoration-line underline-offset-4"
              onClick={() => signOut().then(() => navigate("/"))}
            >
              Sign out
            </button>
          </>
        }
      />

      <main className="pb-16">
        {error && (
          <div className="py-6">
            <Notice tone="error">{error}</Notice>
          </div>
        )}

        {me?.needsOnboarding ? (
          <Onboarding
            email={me.user.email}
            onDone={() => {
              setLoading(true);
              void load();
            }}
          />
        ) : data?.guest.status === "pending" ? (
          <PendingReview name={data.guest.fullName} />
        ) : total === 0 ? (
          <NoEvents />
        ) : (
          <>
            <section className="animate-rise pt-6 pb-10 text-center">
              <p className="eyebrow">Your invitation</p>
              <h1 className="mt-4 font-display text-5xl leading-tight font-light">
                Hello, {firstName}
              </h1>
              <p className="mx-auto mt-5 max-w-md leading-relaxed text-muted">
                {total === 1
                  ? "Here's the event we'd love you at."
                  : `We'd love you at ${total} events across the weekend.`}{" "}
                Reply to each one below by {site.rsvpDeadlineLabel}.
              </p>

              <div className="mx-auto mt-8 flex max-w-xs items-center gap-3">
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-line">
                  <span
                    className="block h-full rounded-full bg-sage transition-all duration-500"
                    style={{ width: `${total ? (answered / total) * 100 : 0}%` }}
                  />
                </span>
                <span className="text-xs whitespace-nowrap text-muted">
                  {answered} of {total} answered
                </span>
              </div>
            </section>

            <div className="space-y-6">
              {data!.events.map((e) => (
                <EventCard
                  key={e.id}
                  event={e}
                  partyMax={data!.guest.partyMax}
                  onSaved={() => void load()}
                />
              ))}
            </div>

            <p className="mt-10 text-center text-sm text-muted">
              Change your mind? Come back any time and update your answers.
            </p>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}
